import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

window.globalPosition = {};

function Canvas(props) {
  const canvasRef = useRef(null);
  const detailsRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [shapes, setShapes] = useState([]);
  const [height, setHeight] = useState(0);
  const [width, setWidth] = useState(0);
  const [currentShape, setCurrentShape] = useState(null);

  useEffect(() => {
    const detailsDiv = detailsRef.current;
    const detailsHeight = detailsDiv.getBoundingClientRect().height;
    const detailsWidth = detailsDiv.getBoundingClientRect().width;

    setHeight(detailsHeight);
    setWidth(detailsWidth);

    setIsDrawing(false);
    setStartX(0);
    setStartY(0);
    setCurrentShape(null);

    const inputs = document.querySelectorAll('.canvas-text-input');
    inputs.forEach(input => {
      if (!input.value.trim()) {
        input.remove();
        return;
      }

      if (props.selectedTool === 'text') {
        input.readOnly = false;
        input.style.pointerEvents = 'auto';
      } else {
        input.readOnly = true;
        input.style.pointerEvents = 'none';
      }
    });
  }, [props.selectedTool]);

  useEffect(() => {
    if (props.selectedTool === 'erase') {
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      setShapes([]);

      const inputs = document.querySelectorAll('.canvas-text-input');
      inputs.forEach((input) => {
        input.parentElement.removeChild(input);
      });

      const highlights = document.querySelectorAll('highlight-span');
      highlights.forEach((highlight) => {
        const parent = highlight.parentNode;
        while (highlight.firstChild) {
          parent.insertBefore(highlight.firstChild, highlight);
        }
        parent.removeChild(highlight);
      });

      context.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, [props.selectedTool]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    shapes.forEach(shape => drawShape(shape));

    if (currentShape) {
      drawShape(currentShape);
    }
  }, [shapes, currentShape]);

  useEffect(() => {
    const inputHandler = (e) => {
      if (e.target.classList.contains('canvas-text-input')) {
        const input = e.target;
        const ctx = document.createElement('canvas').getContext('2d');
        ctx.font = input.style.font;
        const text = input.value || ' ';
        const textWidth = ctx.measureText(text).width + 10;
        const canvas = canvasRef.current;
        const canvasRect = canvas.getBoundingClientRect();
        const inputLeftOuterWidth = parseInt(input.style.left, 10);
        const maxWidth = canvasRect.width - inputLeftOuterWidth;

        if (textWidth > maxWidth) {
          input.value = input.value.slice(0, -1);
          return;
        }

        input.style.width = textWidth + 'px';
      }
    };

    document.addEventListener('input', inputHandler);
    return () => {
      document.removeEventListener('input', inputHandler);
    };
  }, []);

  function drawShape(shape) {
    switch (shape.type) {
    case 'square': {
      drawSquare(shape.startX, shape.startY, shape.endX, shape.endY);
      break;
    }
    case 'circle': {
      drawCircle(shape.startX, shape.startY, shape.endX, shape.endY);
      break;
    }
    case 'arrow': {
      drawArrowPointer(shape.startX, shape.startY, shape.endX, shape.endY);
      break;
    }
    case 'pen': {
      drawPen(shape.points);
      break;
    }
    default:
      break;
    }
  }

  function handleMouseDown(event) {
    setIsDrawing(true);
    setStartX(event.nativeEvent.offsetX);
    setStartY(event.nativeEvent.offsetY);

    if (props.selectedTool !== 'text')
      setCurrentShape({ type: props.selectedTool, x: event.nativeEvent.offsetX, y: event.nativeEvent.offsetY, w: 0, h: 0 });

    if (props.selectedTool === 'square') {
      drawSquare(event.nativeEvent.offsetX, event.nativeEvent.offsetY);
    } else if (props.selectedTool === 'circle') {
      drawCircle(event.nativeEvent.offsetX, event.nativeEvent.offsetY);
    } else if (props.selectedTool === 'text') {
      var inputCount = document.querySelectorAll('.canvas-text-input').length;

      const canvas = canvasRef.current;
      const canvasRect = canvas.getBoundingClientRect();
      const clickX = event.nativeEvent.offsetX;

      const input = document.createElement('input');
      input.type = 'text';
      input.id = 'text_' + (inputCount + 1);
      input.style.position = 'absolute';
      input.style.zIndex = '99';
      input.style.left = `${event.nativeEvent.offsetX}px`;
      input.style.top = `${event.nativeEvent.offsetY}px`;
      input.style.border = 'none';
      input.style.outline = 'none';
      input.style.background = 'none';
      input.style.font = '30px Arial';
      input.style.color = '#000';
      input.style.width = '10px';
      input.readOnly = props.selectedTool !== 'text';
      input.classList.add('canvas-text-input');

      const maxWidth = canvasRect.width - clickX;
      input.style.maxWidth = maxWidth + 'px';

      canvas.parentElement.appendChild(input);
      setTimeout(() => input.focus(), 0);
    } else if (props.selectedTool === 'arrow') {
      drawArrowPointer(startX, startY, event.nativeEvent.offsetX, event.nativeEvent.offsetY);
    } else if (props.selectedTool === 'pen') {
      const newStroke = {
        type: 'pen',
        points: [{ x: event.nativeEvent.offsetX, y: event.nativeEvent.offsetY }]
      };
      setShapes(prev => [...prev, newStroke]);
    }
  }

  function handleMouseMove(event) {
    if (!currentShape) return;

    const { offsetX, offsetY } = event.nativeEvent;
    setCurrentShape(prev => ({
      ...prev,
      w: offsetX - prev.x,
      h: offsetY - prev.y
    }));

    if (isDrawing) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      if (props.selectedTool === 'pen') {
        setShapes(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last && last.type === 'pen') {
            last.points.push({ x: offsetX, y: offsetY });
          }
          return updated;
        });
      }

      if (props.selectedTool !== 'pen') {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        for (const shape of shapes) {
          if (shape.type === 'square') {
            drawSquare(shape.startX, shape.startY, shape.endX, shape.endY);
          } else if (shape.type === 'circle') {
            drawCircle(shape.startX, shape.startY, shape.endX, shape.endY);
          } else if (shape.type === 'arrow') {
            drawArrowPointer(shape.startX, shape.startY, shape.endX, shape.endY);
          } else if (shape.type === 'pen') {
            drawPen(shape.points);
          }
        }
      }

      if (props.selectedTool === 'square') {
        drawSquare(startX, startY, event.nativeEvent.offsetX, event.nativeEvent.offsetY);
      } else if (props.selectedTool === 'circle') {
        drawCircle(startX, startY, event.nativeEvent.offsetX, event.nativeEvent.offsetY);
      } else if (props.selectedTool === 'arrow' && startX !== 0 && startY !== 0) {
        drawArrowPointer(startX, startY, event.nativeEvent.offsetX, event.nativeEvent.offsetY);
      } else if (props.selectedTool === 'erase-content') {
        eraseShape(event.nativeEvent.offsetX, event.nativeEvent.offsetY);
      }
    }
  }

  function drawPen(points) {
    if (!points || points.length < 2) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000';

    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    ctx.stroke();
  }

  function drawSquare(x1, y1, x2, y2) {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = Math.abs(x2 - x1);
    const height = Math.abs(y2 - y1);
    const left = Math.min(x1, x2);
    const top = Math.min(y1, y2);
    ctx.fillStyle = 'rgb(255 255 0 / 47%)';
    ctx.fillRect(left, top, width, height);
    ctx.strokeStyle = '#000';
    ctx.strokeRect(left, top, width, height);
  }

  function drawCircle(x1, y1, x2, y2) {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const radius = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
    ctx.fillStyle = 'rgb(255 255 0 / 47%)';
    ctx.beginPath();
    ctx.arc(x1, y1, radius, 0, 2 * Math.PI);
    ctx.fill();
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  function handleMouseUp(event) {
    setIsDrawing(false);
    setStartX(0);
    setStartY(0);

    if (props.selectedTool !== 'pen') {
      const newShape = {
        type: props.selectedTool,
        startX: startX,
        startY: startY,
        endX: event.nativeEvent.offsetX,
        endY: event.nativeEvent.offsetY
      };

      setShapes(prev => [...prev, newShape]);
    }
    window.globalPosition = {};
  }

  function drawArrowPointer(startX, startY, endX, endY) {
    if (startX === 0 && startY === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const dx = endX - startX;
    const dy = endY - startY;
    const angle = Math.atan2(dy, dx);
    const headlen = 20;

    ctx.beginPath();
    ctx.moveTo(startX, startY);
    ctx.lineTo(endX, endY);
    ctx.lineTo(
      endX - headlen * Math.cos(angle - Math.PI / 6),
      endY - headlen * Math.sin(angle - Math.PI / 6)
    );
    ctx.moveTo(endX, endY);
    ctx.lineTo(
      endX - headlen * Math.cos(angle + Math.PI / 6),
      endY - headlen * Math.sin(angle + Math.PI / 6)
    );
    ctx.stroke();
  }

  function handleHighlightBodyClick() {
    if (props.selectedTool !== 'highlight') return;

    function isHiddenOrNonHighlightable(node) {
      return (node.parentElement && node.parentElement.closest('.d-none') || ['table', 'thead', 'tbody', 'tr'].includes(node.parentElement?.tagName?.toLowerCase()));
    }

    function collectTextNodes(node) {
      if (isHiddenOrNonHighlightable(node)) return;

      if (node.nodeType === Node.TEXT_NODE) {
        if (range.intersectsNode(node)) {
          textNodes.push(node);
        }
      } else {
        for (let child of node.childNodes) {
          collectTextNodes(child);
        }
      }
    }

    const selection = window.getSelection();
    const range = selection.getRangeAt(0);
    const ancestorNode = range.commonAncestorContainer;
    const textNodes = [];
    collectTextNodes(ancestorNode);

    textNodes.forEach(node => {
      const nodeRange = document.createRange();
      nodeRange.selectNodeContents(node);

      if (node === textNodes[0]) nodeRange.setStart(node, range.startOffset);
      if (node === textNodes[textNodes.length - 1]) nodeRange.setEnd(node, range.endOffset);

      const highlightSpan = document.createElement('highlight-span');
      nodeRange.surroundContents(highlightSpan);
    });

    selection.removeAllRanges();
  }

  function eraseShape(x, y) {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const eraserSize = 20;
    ctx.clearRect(x - eraserSize / 2, y - eraserSize / 2, eraserSize, eraserSize);

    const updatedShapes = shapes.filter((shape) => {
      if (shape.type === 'square' || shape.type === 'circle') {
        return !isPointInsideShape(x, y, shape);
      } else if (shape.type === 'arrow') {
        return !isPointOnArrow(x, y, shape);
      } else if (shape.type === 'pen') {
        return !isPointCloseToPen(x, y, shape.points, eraserSize / 2);
      }
      return true;
    });

    setShapes(updatedShapes);

    const inputs = document.querySelectorAll('.canvas-text-input');
    inputs.forEach(input => {
      const rect = input.getBoundingClientRect();
      const canvasRect = canvas.getBoundingClientRect();

      const inputX = rect.left - canvasRect.left;
      const inputY = rect.top - canvasRect.top;
      const inputW = rect.width;
      const inputH = rect.height;
      const overlaps = x >= inputX && x <= inputX + inputW && y >= inputY && y <= inputY + inputH;

      if (overlaps) {
        input.remove();
      }
    });

    const highlights = document.querySelectorAll('highlight-span');
    highlights.forEach(span => {
      const rect = span.getBoundingClientRect();
      const canvasRect = canvas.getBoundingClientRect();

      const highlightX = rect.left - canvasRect.left;
      const highlightY = rect.top - canvasRect.top;
      const highlightW = rect.width;
      const highlightH = rect.height;
      const overlaps = x >= highlightX - eraserSize / 2 && x <= highlightX + highlightW + eraserSize / 2 && y >= highlightY - eraserSize / 2 && y <= highlightY + highlightH + eraserSize / 2;

      if (overlaps) {
        const parent = span.parentNode;
        while (span.firstChild) {
          parent.insertBefore(span.firstChild, span);
        }
        parent.removeChild(span);
      }
    });
  }

  function isPointCloseToPen(x, y, points, tolerance) {
    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];
      const dist = distancePointToSegment(x, y, p1.x, p1.y, p2.x, p2.y);
      if (dist <= tolerance) return true;
    }
    return false;
  }

  function distancePointToSegment(px, py, x1, y1, x2, y2) {
    const A = px - x1;
    const B = py - y1;
    const C = x2 - x1;
    const D = y2 - y1;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = -1;
    if (lenSq !== 0) param = dot / lenSq;

    let xx, yy;
    if (param < 0) {
      xx = x1;
      yy = y1;
    } else if (param > 1) {
      xx = x2;
      yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }

    const dx = px - xx;
    const dy = py - yy;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function isPointInsideShape(x, y, shape) {
    if (shape.type === 'circle') {
      const centerX = shape.startX;
      const centerY = shape.startY;
      const radius = Math.sqrt((shape.endX - shape.startX) ** 2 + (shape.endY - shape.startY) ** 2);
      const dist = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);
      return dist <= radius;
    } else {
      const left = Math.min(shape.startX, shape.endX);
      const right = Math.max(shape.startX, shape.endX);
      const top = Math.min(shape.startY, shape.endY);
      const bottom = Math.max(shape.startY, shape.endY);
      return x >= left && x <= right && y >= top && y <= bottom;
    }
  }

  function isPointOnArrow(x, y, shape, tolerance = 10) {
    const { startX, startY, endX, endY } = shape;
    const dist = distancePointToSegment(x, y, startX, startY, endX, endY);
    return dist <= tolerance;
  }

  return (
    <div style={{ position: 'relative' }}>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className={`${props.selectedTool === 'erase-content' ? 'eraser-cursor' : ''}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        style={{
          zIndex: 9,
          pointerEvents: `${props.selectedTool == 'highlight' ? 'none' : 'auto'}`,
          position: 'absolute'
        }}
      />

      <div
        onClick={handleHighlightBodyClick}
        ref={detailsRef}
        style={{
          zIndex: 1
        }}>
        {props.Details}
      </div>
    </div>
  );
}

Canvas.propTypes = {
  selectedTool: PropTypes.string,
  selectedQuestionId: PropTypes.number,
  Details: PropTypes.element
};

export default Canvas;
